import { NavLink } from "react-router-dom";
import * as Icons from "lucide-react";
import { cn } from "@/lib/utils";
import { groupServices } from "@/utils/groupServices";

export const usd = (n, cur = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(n || 0);
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

export const STATUS_TONES = {
  draft: "neutral", internal_review: "warning", ready_for_review: "warning", ready_to_send: "info", approved: "info",
  sent: "info", viewed: "purple", reminder_sent: "warning", accepted: "success",
  declined: "danger", expired: "danger", superseded: "neutral", converted: "success",
  payment_pending: "warning", paid: "success", voided: "danger", cancelled: "neutral",
};

// Canonical external payment methods (mirror of backend sales.PAYMENT_METHODS).
export const PAYMENT_METHOD_OPTIONS = [
  ["wise", "Wise"], ["bank_transfer", "Bank Transfer"], ["qr_promptpay", "QR / PromptPay"],
  ["cash", "Cash"], ["card", "Credit / Debit Card"], ["cheque", "Cheque"],
  ["paypal", "PayPal"], ["other", "Other"],
];
export const PAYMENT_METHOD_LABELS = Object.fromEntries(PAYMENT_METHOD_OPTIONS);

// Historical acceptance capture methods.
export const ACCEPTANCE_METHOD_OPTIONS = [
  ["historical_record", "Historical Record"], ["email", "Email"], ["conversation", "Historical Conversation"],
  ["verbal", "Verbal Confirmation"], ["portal", "Portal"], ["other", "Other"],
];

const SALES_LINKS = [
  ["/sales/pipeline", "Pipeline", "Kanban"],
  ["/sales/estimates", "Estimates", "Calculator"],
  ["/sales/follow-up", "Follow-Up", "BellRing"],
];

export function SalesNav() {
  return (
    <div className="flex items-center gap-1 border-b border-slate-200 mb-6 overflow-x-auto tf-scroll" data-testid="sales-nav">
      {SALES_LINKS.map(([to, label, icon]) => {
        const Icon = Icons[icon] || Icons.Circle;
        return (
          <NavLink key={to} to={to} data-testid={`sales-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) => cn(
              "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors",
              isActive ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <Icon className="h-4 w-4" />{label}
          </NavLink>
        );
      })}
    </div>
  );
}

/* Line-item editor used by both estimate & proposal builders */
export function LineItemEditor({ items, setItems, services = [], categories = [], addons = [], currency = "USD" }) {
  const add = (type) => {
    const base = { item_type: type, ref_id: null, service_code: null, name: "", description: "", quantity: 1, unit_price: 0, discount: 0, taxable: false };
    setItems([...items, base]);
  };
  const update = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const pick = (i, value) => {
    const [kind, id] = value.split(":");
    const src = kind === "service" ? services.find((s) => s.id === id) : addons.find((a) => a.id === id);
    if (src) setItems(items.map((it, idx) => idx === i ? { ...it, item_type: kind, ref_id: id, service_code: kind === "service" ? (src.code || null) : null, name: src.name, unit_price: src.base_price } : it));
  };
  const lineTotal = (it) => {
    const base = (it.quantity || 0) * (it.unit_price || 0) - (it.discount || 0);
    return it.item_type === "discount" ? -Math.abs(base) : base;
  };
  const subtotal = items.filter((i) => i.item_type !== "discount").reduce((s, i) => s + lineTotal(i), 0);
  const disc = items.filter((i) => i.item_type === "discount").reduce((s, i) => s + Math.abs(lineTotal(i)), 0);

  return (
    <div data-testid="line-item-editor">
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded-lg p-2" data-testid={`line-item-${i}`}>
            <select value={it.ref_id ? `${it.item_type}:${it.ref_id}` : ""} onChange={(e) => pick(i, e.target.value)} data-testid={`line-item-service-${i}`} className="col-span-4 bg-white border border-slate-300 rounded px-2 py-1.5 text-sm">
              <option value="">{it.name || "Select or type custom…"}</option>
              {groupServices(services, categories).map(({ label, items: grpItems }) => (
                <optgroup key={label} label={label}>
                  {grpItems.map((s) => <option key={s.id} value={`service:${s.id}`}>{s.name}</option>)}
                </optgroup>
              ))}
              {addons.length > 0 && <optgroup label="Add-ons">{addons.map((a) => <option key={a.id} value={`addon:${a.id}`}>{a.name}</option>)}</optgroup>}
            </select>
            <input value={it.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Description" className="col-span-3 bg-white border border-slate-300 rounded px-2 py-1.5 text-sm" />
            <input type="number" value={it.quantity} onChange={(e) => update(i, "quantity", parseFloat(e.target.value) || 0)} className="col-span-1 bg-white border border-slate-300 rounded px-2 py-1.5 text-sm" />
            <input type="number" value={it.unit_price} onChange={(e) => update(i, "unit_price", parseFloat(e.target.value) || 0)} className="col-span-2 bg-white border border-slate-300 rounded px-2 py-1.5 text-sm" />
            <span className="col-span-1 text-sm text-right font-medium">{usd(lineTotal(it), currency)}</span>
            <button onClick={() => remove(i)} className="col-span-1 text-slate-400 hover:text-red-500 flex justify-center"><Icons.Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => add("service")} className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1" data-testid="add-line-item"><Icons.Plus className="h-4 w-4" />Add line</button>
        <button onClick={() => add("discount")} className="text-sm text-slate-500 hover:underline inline-flex items-center gap-1" data-testid="add-discount"><Icons.Minus className="h-4 w-4" />Add discount</button>
      </div>
      <div className="flex justify-end gap-8 mt-3 pt-3 border-t border-slate-100 text-sm">
        <div className="text-slate-500">Subtotal <span className="font-medium text-slate-800 ml-2">{usd(subtotal, currency)}</span></div>
        <div className="text-slate-500">Discount <span className="font-medium text-rose-600 ml-2">-{usd(disc, currency)}</span></div>
        <div className="text-slate-700 font-semibold">Total <span className="ml-2">{usd(subtotal - disc, currency)}</span></div>
      </div>
    </div>
  );
}
